import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, BadgeDollarSign, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PublicNavbar } from "@/components/public-navbar";
import { useUser } from "@/queries/user";
import { usePricing, type PricePoint } from "@/queries/billing";
import { SeoHead } from "@/components/seo-head";
import { JsonLd } from "@/components/json-ld";

const PRODUCT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "TodoLLM",
  url: "https://todollm.com/pricing",
  description:
    "Task engine for hybrid human-AI teams. Start free, scale to Pro or Team.",
  offers: [
    {
      "@type": "Offer",
      name: "Free",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "2",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Team",
      price: "7",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
  ],
};

type PlanId = "free" | "pro" | "team";

function formatPrice(
  p: PricePoint | null | undefined,
  fallbackCents: number,
): string {
  const cents = p?.amount_cents ?? fallbackCents;
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

export function PricingPage() {
  const user = useUser();
  const pricing = usePricing();
  const navigate = useNavigate();
  const isSignedIn = Boolean(user.data);

  const proBase = formatPrice(pricing.data?.task_base, 200);
  const orgBase = formatPrice(pricing.data?.organization, 700);
  const seat = formatPrice(pricing.data?.seat, 200);
  const teamMin = orgBase;

  if (pricing.isError) {
    console.warn(pricing.error);
  }

  const continueWith = (plan: PlanId) => {
    if (isSignedIn) {
      // Pro/Team go to /billing so the user can pick up the existing
      // "Upgrade to Pro" or "Create a Team" CTA there.
      if (plan === "pro" || plan === "team") {
        navigate({ to: "/billing" });
      } else {
        navigate({ to: "/onboarding", search: { plan } });
      }
    } else {
      const next = plan === "free" ? "/onboarding" : "/billing";
      navigate({ to: "/sign-up", search: { next, plan } });
    }
  };

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Pricing"
        description="Start free, scale when you need to. TodoLLM offers a free tier for personal projects, Pro for solo builders, and Team plans for shared organizations and agent workflows."
        canonical="https://todollm.com/pricing"
      />
      <JsonLd data={PRODUCT_SCHEMA} />
      <PublicNavbar />
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:px-6">
        <header className="space-y-4">
          <p className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            <BadgeDollarSign className="size-3.5" />
            Pricing
          </p>
          <h1 className="max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl">
            Simple pricing for solo builders and multi-agent teams.
          </h1>
          <p className="max-w-2xl text-base leading-7 text-muted-foreground">
            Start free, scale to Pro for more projects, or move to Team when you
            need shared organizations and collaborators.
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-3">
          {pricing.error && (
            <div className="lg:col-span-3 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              Live pricing unavailable - showing baseline rates.
            </div>
          )}
          <PlanCard
            name="Free"
            price="$0"
            description="For getting started with personal projects."
            features={[
              "Up to 4 projects",
              "Up to 50 tasks per month",
              "Personal use only (no organizations)",
            ]}
            cta="Continue with Free Plan"
            onSelect={() => continueWith("free")}
          />
          <PlanCard
            name="Pro"
            price={`From ${proBase}/month`}
            description="For solo builders running multiple projects and agents."
            features={[
              "Unlimited projects",
              `${proBase}/month includes 100 tasks, then $0.02/task`,
              "Personal use only (no organizations)",
            ]}
            cta="Continue with Pro Plan"
            onSelect={() => continueWith("pro")}
            featured
          />
          <PlanCard
            name="Team"
            price={`From ${teamMin}/month`}
            description="For teams running shared organizations and agent workflows."
            features={[
              `${orgBase}/month per organization (1 owner seat + 100 tasks included)`,
              `${seat}/month per additional member`,
              `${proBase}/month includes 100 tasks, then $0.02/task`,
              "Unlimited projects in the organization",
            ]}
            cta="Continue with Team Plan"
            onSelect={() => continueWith("team")}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Need to see it in action first?</CardTitle>
            <CardDescription>
              Browse the docs to see how TodoLLM coordinates agents across
              projects.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/docs">
                Read the docs
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PlanCard({
  name,
  price,
  description,
  features,
  cta,
  onSelect,
  featured = false,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  onSelect: () => void;
  featured?: boolean;
}) {
  return (
    <Card
      className={
        featured ? "border-primary/40 shadow-lg shadow-primary/10" : ""
      }
    >
      <CardHeader>
        <CardTitle className="text-2xl">{name}</CardTitle>
        <p className="text-3xl font-semibold tracking-tight">{price}</p>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {features.map((feature) => (
            <div key={feature} className="flex items-start gap-3 text-sm">
              <Check className="mt-0.5 size-4 text-primary" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
        <Button
          onClick={onSelect}
          className="w-full"
          variant={featured ? "default" : "outline"}
        >
          {cta}
          <ArrowRight className="size-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
